import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type Ref,
  type RefObject,
} from 'react'
import { cx } from '@/shared/lib'
import { Icon } from '@/shared/ui/Icon'
import { IconButton } from '@/shared/ui/IconButton'
import s from './ScrollToTop.module.css'

/**
 * Где сидит кнопка. `viewport` — угол экрана (`fixed`), каркас приложения;
 * `container` — угол ближайшего позиционированного предка (`absolute`), виджет со своей
 * областью прокрутки. Ось независима от того, что слушается: таблица на телефоне занимает
 * весь экран — слушать надо её, а сидеть кнопка обязана в углу экрана, над домашним индикатором.
 */
export type ScrollToTopPlacement = 'viewport' | 'container'

export type ScrollToTopProps = {
  /** Доступное имя, обязательно: кнопка без подписи. Уходит в `aria-label`. */
  label: string
  /** Область прокрутки. Не передана — окно документа. */
  scrollTargetRef?: RefObject<HTMLElement | null>
  /**
   * Куда увести фокус после прыжка — обычно заголовок страницы `<h1 tabIndex={-1}>`.
   * Не передан — кнопка снимает фокус с себя: следующий Tab пойдёт с начала документа,
   * ровно оттуда, куда пользователь приехал глазами.
   */
  focusTargetRef?: RefObject<HTMLElement | null>
  placement?: ScrollToTopPlacement
  /** Сколько высот видимой области нужно прокрутить, чтобы кнопка появилась. */
  revealAfterScreens?: number
  /** Ведёт на саму кнопку: это единственный узел, который можно сфокусировать. */
  ref?: Ref<HTMLButtonElement>
  /* disabled и type изъяты: выключенной кнопки «наверх» не бывает, а плавающий контрол
     не отправляет форму. aria-label исключён — имя задаётся ровно одним способом, пропом label. */
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'color' | 'aria-label' | 'disabled' | 'type'
>

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const PLACEMENT_CLASS: Record<ScrollToTopPlacement, string | undefined> = {
  viewport: s.viewport,
  container: s.container,
}

/**
 * Доля порога, ниже которой кнопка прячется. Мёртвая зона в четверть экрана шире дребезга
 * инерционной прокрутки и незаметна в работе: за один жест палец проходит её насквозь.
 */
const HIDE_RATIO = 0.75

/**
 * Плавная прокрутка — это движение на несколько экранов, ровно то, от чего защищает системная
 * настройка. Глобальный блок base.css сюда не дотягивается: `scrollTo` — JS-API, а не переход.
 * Читается в момент нажатия: подписка ради значения, нужного один раз в клике, — лишний
 * слушатель и лишняя перерисовка. В jsdom matchMedia не реализован, и отсутствие API
 * не должно ронять ни тесты, ни экзотический WebView (та же защита, что у ResizeObserver
 * в Popover).
 */
function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Прокрутка к началу области. `left` не передаётся намеренно: «наверх» — это вертикаль.
 * Матрица «склады × позиции» шире экрана, и сброс scrollLeft отобрал бы у пользователя
 * колонку, которую он смотрел.
 *
 * Вынесено из компонента: `Element.scrollTo` нет в jsdom и в древних движках, а запасной
 * путь через `scrollTop` — присваивание в узел, пришедший пропом.
 */
function scrollToStart(container: HTMLElement | null, behavior: ScrollBehavior) {
  if (!container) {
    window.scrollTo({ top: 0, behavior })
    return
  }

  if (typeof container.scrollTo === 'function') container.scrollTo({ top: 0, behavior })
  else container.scrollTop = 0
}

/**
 * Плавающая кнопка «наверх»: возврат к началу длинной области одним нажатием.
 * Появляется, когда пройден первый экран, и уходит, когда возвращаться некуда.
 *
 * Слушает окно или переданный контейнер (в виртуализованной таблице прокручивается не окно).
 * Слушатель пассивный, позиция читается в `requestAnimationFrame`, состояние меняется только
 * при смене «показывать / не показывать» — за прокрутку всей страницы каталога компонент
 * перерисовывается дважды.
 *
 * Контракт: docs/ui-kit/contracts/scroll-to-top.md
 */
export function ScrollToTop({
  label,
  scrollTargetRef,
  focusTargetRef,
  placement = 'viewport',
  revealAfterScreens = 1,
  className,
  onClick,
  ref,
  ...rest
}: ScrollToTopProps) {
  const [visible, setVisible] = useState(false)

  /*
   * Предыдущее значение живёт в ref, а не в состоянии: сравнение не должно зависеть от того,
   * успел ли React применить прошлый рендер, иначе на быстрой прокрутке setState полетит
   * несколько раз подряд с одним и тем же значением.
   */
  const visibleRef = useRef(false)

  useEffect(() => {
    const container = scrollTargetRef?.current ?? null

    /*
     * Контейнер захватывается один раз на монтировании. Типовой порядок в гриде обратный:
     * сначала скелетон, потом таблица, — и тогда ref пуст, слушатель остаётся на окне,
     * а в окне прокрутки нет, то есть кнопка не появится никогда и без единого признака.
     */
    if (import.meta.env.DEV && scrollTargetRef !== undefined && container === null) {
      console.warn(
        'ScrollToTop: scrollTargetRef пуст на монтировании — слушатель ушёл на window. ' +
          'Монтируй кнопку вместе с контейнером прокрутки, а не раньше него.',
      )
    }

    const read = () => {
      const top = container ? container.scrollTop : window.scrollY
      const frame = container ? container.clientHeight : window.innerHeight
      const revealAt = frame * revealAfterScreens

      // Гистерезис: показываемся на полном пороге, прячемся на 75 % от него
      const next = visibleRef.current ? top > revealAt * HIDE_RATIO : top > revealAt

      if (next === visibleRef.current) return
      visibleRef.current = next
      setVisible(next)
    }

    let frameId = 0
    const schedule = () => {
      if (frameId !== 0) return
      frameId = requestAnimationFrame(() => {
        frameId = 0
        read()
      })
    }

    /*
     * Стартовое чтение: страница могла открыться уже прокрученной — возврат по «назад»,
     * перезагрузка с восстановлением позиции, переход по якорю. Ждать первого события scroll
     * значит не показать кнопку тому, кто и так уже внизу.
     */
    read()

    // Слушатель на самом источнике: он известен точно, чужие прокрутки кнопке не нужны.
    // passive: обработчик никогда не зовёт preventDefault, и ждать его браузер не обязан.
    const source: EventTarget = container ?? window
    source.addEventListener('scroll', schedule, { passive: true })

    // Порог считается в высотах кадра — поворот телефона его меняет
    window.addEventListener('resize', schedule, { passive: true })

    return () => {
      if (frameId !== 0) cancelAnimationFrame(frameId)
      source.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [scrollTargetRef, revealAfterScreens])

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)

    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth'
    scrollToStart(scrollTargetRef?.current ?? null, behavior)

    /*
     * Прокрутка не переносит фокус: без этого пользователь клавиатуры смотрит на начало
     * страницы, а следующий Tab продолжает обход снизу. preventScroll — иначе браузер доведёт
     * цель до кадра мгновенно и убьёт плавную прокрутку (в Safari 14 опция игнорируется,
     * деградация — мгновенный прыжок).
     */
    const focusTarget = focusTargetRef?.current
    if (focusTarget?.isConnected === true) focusTarget.focus({ preventScroll: true })
    else event.currentTarget.blur()
  }

  return (
    /*
     * Обёртка держит материал и положение, кнопка — сам контрол. Разведены не ради узла:
     * IconButton занимает transform под нажатие (scale), а появлению нужен свой (translateY);
     * на одном элементе они бы затёрли друг друга. А тень парения и размытие иначе пришлось
     * бы навязывать IconButton поверх его собственных --ib-*, с исходом по порядку файлов.
     */
    <div
      className={cx(s.root, PLACEMENT_CLASS[placement], visible && s.visible, className)}
      data-visible={visible ? 'true' : 'false'}
      data-placement={placement}
    >
      <IconButton
        {...rest}
        ref={ref}
        variant="secondary"
        size="lg"
        icon={<Icon name="arrowUp" size="md" />}
        label={label}
        /* Страховка на случай, если visibility перебьют снаружи: фантомной остановки
           табуляции быть не должно. aria-hidden не ставится — см. решение 4 контракта. */
        tabIndex={visible ? undefined : -1}
        onClick={handleClick}
      />
    </div>
  )
}
